import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { areNumbersWithinDecimalTolerance, roundWithDecimals } from '@commons/utils/math';
import {
  Button,
  Checkbox,
  Dropdown,
  HoverTooltip,
  INPUT_WIDTH,
  InputIncrement,
  Tooltip,
} from '@commons/utils/styledLibraryComponents';
import {
  computeTypesOfPackaging,
  convertPriceToPackagingById,
  convertQuantityToPackagingById,
} from '@commons/utils/packagings';
import { DisabledTooltip } from '@commons/DisabledTooltip';
import { formatTextNumberInputValue, sortPackagings } from '@commons/utils/format';
import { getIsCentralMode } from '@commons/utils/localStorage';
import { SingleDatePicker } from '@commons/DatePickers/SingleDatePicker';
import DisplayNumber, { getNumberOfDecimals, PRICE_NUMBER_DECIMALS } from '@commons/DisplayNumber';
import Footer from '@commons/Footer/Footer';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { Input } from '@lib/inpulse/Input';

import { canEditSentOrReceivedOrder } from '@selectors/actions/orderActions';
import { getAuthorizedActions } from '@selectors/featureProps';

import {
  MAX_CREDIT_ORDER_PICTURE_PER_ORDER,
  ORDER_SOURCES,
  PRODUCT_ORDER_TYPE,
} from '@orders/OrderList/constants';

import { getTranslatedOrderAnomalies } from '@orders/OrderList/components/OrderForm/constants/anomalies';
import MultiplePictureUpload from '@orders/OrderList/components/OrderForm/components/OrderFormInfoPanel/components/MultiplePictureUpload';
import ORDER_STATUS, {
  ORDER_PREPARATION_STATUS,
} from '@orders/OrderList/components/OrderForm/constants/status';

import {
  AnomaliePictureLabel,
  ButtonsContainer,
  Container,
  ContentContainer,
  DetailContainer,
  DisplayNameHovered,
  Divider,
  DrawerText,
  DropdownContainer,
  ImgAddedReference,
  InlineContainer,
  InputIncrementContainer,
  InputIncrementLabel,
  List,
  Name,
  NameContainer,
  RemoveReference,
  RemoveReferenceContainer,
  ShelfLifeDatePickerLabelContainer,
  Sku,
  Thumbnail,
  ThumbnailContainer,
  TitleContainer,
  TooltipContainer,
} from '../styledComponents';

const GENERIC_PRODUCT_INPULSE_ICON = '/images/icon-generic-inpulse-product.svg';
const MAXIMUM_CHARACTER_BEFORE_SHOWING_BIGGER_NAME = 40;
const NUMBER_CHECK_REGEXP = new RegExp(/^\d+($|(\.|\,)(\d+|$))$/);

const OrderReceptionDrawerContent = (props) => {
  const {
    currency,
    handleInputChange,
    handleReceptionInputChange,
    handleReceptionAnomaly,
    hasBLI,
    isEditingPreparationOrder,
    source,
    onClose,
    preparationStatus,
    priceBDL,
    product,
    readOnly,
    setPriceBDL,
    orderStatus,
    authorizedActions,
    deleteProductOrder,
    setOpenStockInfo,
    allCreditPicturesHandlers,
    startDate,
  } = props;

  const [selectedDate, setSelectedDate] = useState(
    product.expirationDate === null ? null : moment(product.expirationDate),
  );

  const orderAnomalies = getTranslatedOrderAnomalies(false);
  const isReception = [ORDER_STATUS.SENT, ORDER_STATUS.RECEPTION].includes(orderStatus);

  const isCentralMode = getIsCentralMode();

  const isEditingPreparedOrder = preparationStatus > ORDER_PREPARATION_STATUS.PREPARING;

  const isPOSEditingPreparationOrder =
    source === ORDER_SOURCES.ORDERS && preparationStatus !== null;

  const isRemoveAddedReferenceDisabled =
    isEditingPreparedOrder || (preparationStatus != null && !isCentralMode);

  const { packagingUsedInOrder, packagingUsedInInvoice, packagingUsedInReception } =
    computeTypesOfPackaging(product.receptionPackagingId, product.packagings);

  const [displayBigger, setDisplayBigger] = useState(false);
  const [disableAnomalyDropdown, setDisableAnomalyDropdown] = useState(
    orderStatus >= ORDER_STATUS.COMPLIANT,
  );
  const [receptionPackagingData, setReceptionPackagingData] = useState({
    packaging: packagingUsedInReception,
    invoiced: product.invoiced,
    received: product.received,
  });

  const [invoicedPriceData, setInvoicedPriceData] = useState({
    price: convertPriceToPackagingById(
      priceBDL,
      packagingUsedInReception.id,
      packagingUsedInInvoice.id,
      product.packagings,
    ),
    overwriteSupplierProductPrice: product.hasOverwrittenOrderedProductPrice,
  });
  const [anomaly, setAnomaly] = useState({});
  const [anomalyPictures, setAnomalyPictures] = useState([]);
  const [isValidationDisabled, setIsValidationDisabled] = useState(false);
  const [errorState, setErrorState] = useState(false);

  /** PRODUCT ORDER ANOMALY PICTURE */
  const selectedPictureRef = useRef();

  const [displayDropdownActions, setDisplayDropdownActions] = useState(false);
  const [isSizeTooHeavy, setIsSizeTooHeavy] = useState(false);

  useEffect(() => {
    if (isEmpty(anomaly) && !disableAnomalyDropdown && !!errorState) {
      setIsValidationDisabled(true);
      return;
    }

    setIsValidationDisabled(readOnly);
    setErrorState(false);
  }, [anomaly, readOnly, disableAnomalyDropdown]);

  useEffect(() => {
    setReceptionPackagingData({
      ...receptionPackagingData,
      packaging: {
        id: packagingUsedInReception.id,
        value: packagingUsedInReception.name,
      },
    });
  }, [product.receptionPackagingId]);

  useEffect(() => {
    const orderedPriceConvertedInInvoicePackaging = convertPriceToPackagingById(
      product.price,
      packagingUsedInOrder.id,
      packagingUsedInInvoice.id,
      product.packagings,
    );

    const invoicedQuantityInInvoicePackaging = convertQuantityToPackagingById(
      receptionPackagingData.invoiced,
      receptionPackagingData.packaging.id,
      packagingUsedInInvoice.id,
      product.packagings,
    );

    // The gap rule applies to the line total (quantity x unit price): a unit price gap
    // below the currency tolerance can still be significant once multiplied by the quantity
    const hasInsignificantDiff = areNumbersWithinDecimalTolerance(
      invoicedQuantityInInvoicePackaging * invoicedPriceData.price,
      invoicedQuantityInInvoicePackaging * orderedPriceConvertedInInvoicePackaging,
      currency.numberDecimals,
    );

    if (
      receptionPackagingData.invoiced === receptionPackagingData.received &&
      (hasInsignificantDiff || invoicedPriceData.overwriteSupplierProductPrice)
    ) {
      setDisableAnomalyDropdown(true);
      hasSelectedAnomaly({});
      return;
    }

    setDisableAnomalyDropdown(false);
  }, [
    receptionPackagingData.invoiced,
    receptionPackagingData.received,
    invoicedPriceData.price,
    invoicedPriceData.overwriteSupplierProductPrice,
  ]);

  useEffect(() => {
    if (isEmpty(product) && product.receptionAnomaly) {
      return;
    }

    setAnomaly(orderAnomalies.find(({ id }) => id === product.receptionAnomaly));
  }, []);

  useEffect(() => {
    const loadedFileData = get(product, 'creditOrderPictureUrl.data', null);

    // If the picture is not uploaded on our S3
    if (loadedFileData) {
      setAnomalyPictures([loadedFileData]);
      return;
    }

    if (product.creditOrderPictureUrl) {
      setAnomalyPictures([product.creditOrderPictureUrl]);
    }
  }, [product.creditOrderPictureUrl]);

  const buttonStyle = { padding: '0px 40px' };

  const handleReceptionPackagingChange = (item) => {
    const updatedInvoicedQty = convertQuantityToPackagingById(
      receptionPackagingData.invoiced,
      receptionPackagingData.packaging.id,
      item.id,
      product.packagings,
    );

    const updatedReceivedQty = convertQuantityToPackagingById(
      receptionPackagingData.received,
      receptionPackagingData.packaging.id,
      item.id,
      product.packagings,
    );

    setReceptionPackagingData({
      ...receptionPackagingData,
      packaging: { id: item.id, value: item.name },
      invoiced: Math.round(updatedInvoicedQty * 100) / 100,
      received: Math.round(updatedReceivedQty * 100) / 100,
    });
  };

  const handleReceptionPriceChange = (e) => {
    const value = e.target?.value;

    if (!value || !value.length) {
      setInvoicedPriceData({ ...invoicedPriceData, price: 0 });

      return;
    }

    if (!NUMBER_CHECK_REGEXP.test(value)) {
      return;
    }

    const formattedValue = formatTextNumberInputValue(value, 0);
    const nbDecimals = getNumberOfDecimals(formattedValue);

    // Need this condition because while user is typing, we don't want to block user typing number without decimals (ex: force entered 3 to 3.00)
    // Keep a raw numeric value in state: a grouped display string would break the input regex and the packaging price computations
    setInvoicedPriceData({
      ...invoicedPriceData,
      price:
        nbDecimals >= PRICE_NUMBER_DECIMALS
          ? roundWithDecimals(formattedValue, PRICE_NUMBER_DECIMALS)
          : formattedValue,
    });
  };

  const handleValidation = () => {
    if (isEmpty(anomaly) && !disableAnomalyDropdown && !isEditingPreparationOrder) {
      setErrorState(true);
      setIsValidationDisabled(true);
      return;
    }

    handleInputChange(receptionPackagingData.invoiced, PRODUCT_ORDER_TYPE.INVOICED);
    handleInputChange(receptionPackagingData.received, PRODUCT_ORDER_TYPE.RECEIVED);

    handleReceptionInputChange('receptionPackagingId', receptionPackagingData.packaging.id);

    if (isReception && canEditSentOrReceivedOrder(authorizedActions)) {
      handleReceptionInputChange('checked', true);
    }

    const convertedPriceToReceptionPackaging = convertPriceToPackagingById(
      invoicedPriceData.price,
      packagingUsedInInvoice.id,
      receptionPackagingData.packaging.id,
      product.packagings,
    );

    // Convert price to packagingUsedInReception for data consistency
    setPriceBDL(convertedPriceToReceptionPackaging);

    // allows to notify whether PO.price of ordered type must be updated when calling backend
    handleReceptionInputChange(
      'hasOverwrittenOrderedProductPrice',
      invoicedPriceData.overwriteSupplierProductPrice,
    );

    const convertedPriceToOrderPackaging = convertPriceToPackagingById(
      invoicedPriceData.price,
      packagingUsedInInvoice.id,
      packagingUsedInOrder.id,
      product.packagings,
    );

    if (invoicedPriceData.overwriteSupplierProductPrice || product.addedAfterOrdered) {
      handleReceptionInputChange('price', convertedPriceToOrderPackaging);
    }

    onClose();
  };

  const hasSelectedAnomaly = (anomalySelected) => {
    setAnomaly(anomalySelected);
    handleReceptionAnomaly(anomalySelected);
  };

  // trick allowing to display priceBDL instead of priceBDC if checkbox selected
  // even if the user has not yet saved
  const formatPriceBDC = () => {
    if (!product.price) {
      return 0;
    }
    if (product.hasOverwrittenOrderedProductPrice) {
      return product.priceBDL;
    }
    return product.price;
  };

  const handleRemoveAddedReference = () => {
    if (isRemoveAddedReferenceDisabled) {
      return;
    }

    setOpenStockInfo({
      entity: null,
      category: null,
    });
    deleteProductOrder(product.id);
  };

  const onSelectedPictureChange = async (uploadedFiles) => {
    const { allCreditPicturesCount, setAllCreditPicturesCount } = allCreditPicturesHandlers;

    if (!!uploadedFiles && uploadedFiles.length) {
      handleReceptionInputChange('creditOrderPictureUrl', uploadedFiles[0]);

      // Check if it's not a picture replacement
      if (isEmpty(anomalyPictures)) {
        setAllCreditPicturesCount(allCreditPicturesCount + 1);
      }

      return;
    }

    // If uploadedFiles is undefined, it means the user has removed the picture
    handleReceptionInputChange('creditOrderPictureUrl', null);
    if (allCreditPicturesCount > 0) {
      setAllCreditPicturesCount(allCreditPicturesCount - 1);
    }
  };

  const onShelfLifeDateChange = (date) => {
    setSelectedDate(date);
    handleReceptionInputChange('expirationDate', date);
  };

  const getDisplayedPrice = (value) => {
    const nbDecimals = getNumberOfDecimals(value);

    return nbDecimals >= PRICE_NUMBER_DECIMALS
      ? roundWithDecimals(value, PRICE_NUMBER_DECIMALS)
      : value;
  };

  return (
    <Container>
      <ContentContainer>
        <TitleContainer>
          {i18next.t(
            isEditingPreparationOrder
              ? 'ORDERS.ORDERS.DRAWER_TITLE_PREPARATION'
              : 'ORDERS.ORDERS.DRAWER_TITLE_RECEPTION',
          )}
        </TitleContainer>

        <List>
          <InlineContainer>
            <ThumbnailContainer>
              {product.addedAfterOrdered && <ImgAddedReference />}
              <Thumbnail src={product.img || GENERIC_PRODUCT_INPULSE_ICON} />
            </ThumbnailContainer>
            <NameContainer>
              <Name onMouseOver={() => setDisplayBigger(true)}>{product.name}</Name>
              {product.sku && <Sku>{product.sku}</Sku>}
              {displayBigger &&
                product.name.length > MAXIMUM_CHARACTER_BEFORE_SHOWING_BIGGER_NAME && (
                  <DisplayNameHovered onMouseOut={() => setDisplayBigger(false)}>
                    {product.name}
                  </DisplayNameHovered>
                )}
            </NameContainer>
          </InlineContainer>
          <DetailContainer>
            <DrawerText isLight>
              {i18next.t('ORDERS.ORDERS.FORM_COLUMN_LABEL_ORDERED')} :
            </DrawerText>
            <DrawerText isBold>{`${product.ordered} x ${packagingUsedInOrder.name}`}</DrawerText>
          </DetailContainer>
          <DetailContainer style={{ marginTop: '16px' }}>
            <DrawerText isLight>
              {`${i18next.t('ORDERS.ORDERS.FORM_COLUMN_LABEL_PRICE')} (${currency.alphabeticCode}/${
                packagingUsedInOrder.name
              }) :`}
            </DrawerText>
            <DisplayNumber
              color={ENUM_COLORS.DARKEST}
              customNumberDecimal={PRICE_NUMBER_DECIMALS}
              displayCurrencyCode={true}
              font={ENUM_FONTS.TEXT_MIDDLE_BOLD}
              number={formatPriceBDC()}
            />
          </DetailContainer>
        </List>

        <Divider />

        <List>
          <DrawerText
            isDisabled={readOnly || isEditingPreparedOrder || isPOSEditingPreparationOrder}
          >
            {i18next.t('GENERAL.PACKAGING')} :
          </DrawerText>
          <DetailContainer style={{ marginTop: '8px' }}>
            <HoverTooltip
              isTooltipDisplayed={isEditingPreparationOrder && orderStatus > ORDER_STATUS.RECEPTION}
              renderContent={() => (
                <DisabledTooltip
                  text={i18next.t('ORDERS.ORDERS.DRAWER_TOOLTIP_ALREADY_RECEIVED_PREPARATION')}
                />
              )}
            >
              <Dropdown
                isDisabled={readOnly || isEditingPreparedOrder || isPOSEditingPreparationOrder}
                items={product.packagings}
                selectedItem={receptionPackagingData.packaging}
                sortBy={sortPackagings}
                width={INPUT_WIDTH.EXTRA_LARGE}
                isRequired
                onSelectionChange={handleReceptionPackagingChange}
              />
            </HoverTooltip>
          </DetailContainer>
          <DetailContainer style={{ gap: '24px' }} inline>
            <InputIncrementContainer>
              <InputIncrementLabel
                isDisabled={
                  readOnly || isEditingPreparedOrder || isPOSEditingPreparationOrder || hasBLI
                }
              >
                {i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_BDL')} :
              </InputIncrementLabel>
              <HoverTooltip
                isTooltipDisplayed={
                  isEditingPreparationOrder && orderStatus > ORDER_STATUS.RECEPTION
                }
                renderContent={() => (
                  <DisabledTooltip
                    text={i18next.t('ORDERS.ORDERS.DRAWER_TOOLTIP_ALREADY_RECEIVED_PREPARATION')}
                  />
                )}
              >
                <InputIncrement
                  isDisabled={
                    readOnly || isEditingPreparedOrder || isPOSEditingPreparationOrder || hasBLI
                  }
                  limitValues={{ min: 0 }}
                  setValue={(value) =>
                    setReceptionPackagingData({ ...receptionPackagingData, invoiced: value })
                  }
                  value={receptionPackagingData.invoiced}
                  width={'100%'}
                />
              </HoverTooltip>
            </InputIncrementContainer>
            {!isEditingPreparationOrder && (
              <InputIncrementContainer>
                <InputIncrementLabel isDisabled={readOnly}>
                  {i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_RECEIVED')} :
                </InputIncrementLabel>
                <InputIncrement
                  isDisabled={readOnly}
                  limitValues={{ min: 0 }}
                  setValue={(value) =>
                    setReceptionPackagingData({ ...receptionPackagingData, received: value })
                  }
                  value={receptionPackagingData.received}
                  width={'100%'}
                />
              </InputIncrementContainer>
            )}
          </DetailContainer>
          {!isEditingPreparationOrder && (
            <DetailContainer>
              <ShelfLifeDatePickerLabelContainer>
                <Text color={ENUM_COLORS.IP_BLACK_1}>
                  {i18next.t('ORDERS.ORDERS.SHELF_LIFE_DRAWER_INPUT_LABEL')}
                </Text>
                <TooltipContainer>
                  <Tooltip text={i18next.t('ORDERS.ORDERS.SHELF_LIFE_TOOLTIP_DRAWER')} />
                </TooltipContainer>
              </ShelfLifeDatePickerLabelContainer>
              <SingleDatePicker
                date={selectedDate}
                disabled={readOnly}
                maxPastDate={startDate}
                placeholder={i18next.t('GENERAL.DATEPICKER_PLACEHOLDER')}
                width={'500px'}
                onDateChange={onShelfLifeDateChange}
              />
            </DetailContainer>
          )}
        </List>

        <Divider />

        <List>
          <DrawerText
            isDisabled={
              readOnly || isEditingPreparedOrder || isPOSEditingPreparationOrder || hasBLI
            }
          >{`${i18next.t('ORDERS.ORDERS.LIST_LABEL_PRICE_UNIT_EX_TAX_BDL')} (${
            currency.alphabeticCode
          }/${packagingUsedInInvoice.name}) :`}</DrawerText>
          <DetailContainer style={{ marginTop: '8px' }}>
            <HoverTooltip
              isTooltipDisplayed={isEditingPreparationOrder && orderStatus > ORDER_STATUS.RECEPTION}
              renderContent={() => (
                <DisabledTooltip
                  text={i18next.t('ORDERS.ORDERS.DRAWER_TOOLTIP_ALREADY_RECEIVED_PREPARATION')}
                />
              )}
            >
              <Input
                autoComplete="off"
                name="BDLPrice"
                readOnly={
                  readOnly || isEditingPreparedOrder || isPOSEditingPreparationOrder || hasBLI
                }
                type="text"
                value={getDisplayedPrice(invoicedPriceData.price)}
                width={INPUT_WIDTH.EXTRA_LARGE}
                textBig
                useParentHeight
                onChange={handleReceptionPriceChange}
              />
            </HoverTooltip>
          </DetailContainer>
          {orderStatus <= ORDER_STATUS.RECEPTION &&
            product.priceEditableAtReception &&
            !isEditingPreparationOrder && (
              <HoverTooltip
                isTooltipDisplayed={!packagingUsedInInvoice.isActive}
                renderContent={() => (
                  <DisabledTooltip
                    text={i18next.t(
                      'ORDERS.ORDERS.DRAWER_RECEPTION_PRICE_BDL_CHECKBOX_DISABLED_TOOLTIP',
                    )}
                  />
                )}
              >
                <DetailContainer inline>
                  <Checkbox
                    handleClick={() =>
                      setInvoicedPriceData({
                        ...invoicedPriceData,
                        overwriteSupplierProductPrice:
                          !invoicedPriceData.overwriteSupplierProductPrice,
                      })
                    }
                    isChecked={invoicedPriceData.overwriteSupplierProductPrice}
                    isDisabled={
                      readOnly ||
                      !packagingUsedInInvoice.isActive ||
                      isEditingPreparedOrder ||
                      isPOSEditingPreparationOrder
                    }
                    shape="square"
                  />
                  <DrawerText
                    isDisabled={
                      readOnly ||
                      !packagingUsedInInvoice.isActive ||
                      isEditingPreparedOrder ||
                      isPOSEditingPreparationOrder
                    }
                  >
                    {i18next.t('ORDERS.ORDERS.DRAWER_RECEPTION_PRICE_BDL_CHECKBOX_LABEL')}
                  </DrawerText>
                </DetailContainer>
              </HoverTooltip>
            )}
        </List>

        {!isEditingPreparationOrder && (
          <>
            <Divider />
            <List>
              <DropdownContainer>
                <Dropdown
                  isDisabled={disableAnomalyDropdown || readOnly}
                  isErrorState={errorState}
                  items={orderAnomalies}
                  label={`${i18next.t('ORDERS.ORDERS.LIST_LABEL_ANOMALY_REASON')} :`}
                  placeholder={i18next.t('GENERAL.CHOOSE_PLACEHOLDER')}
                  selectedItem={anomaly}
                  width={INPUT_WIDTH.EXTRA_LARGE}
                  isRequired
                  onSelectionChange={(selectedItem) => hasSelectedAnomaly(selectedItem)}
                />
                {errorState && (
                  <Text
                    color={ENUM_COLORS.INFO_RED}
                    font={ENUM_FONTS.TEXT_SMALL_HEIGHT_14_WEIGHT_600}
                  >
                    {i18next.t('ADMIN.SUPPLIER_PRODUCTS.DETAIL_FORM_FIELD_REQUIRED_ERROR', {
                      field: i18next.t('ORDERS.ORDERS.LIST_LABEL_ANOMALY_REASON'),
                    })}
                  </Text>
                )}
              </DropdownContainer>
            </List>

            <List>
              <AnomaliePictureLabel>
                <Text
                  color={
                    disableAnomalyDropdown || readOnly ? ENUM_COLORS.DARK : ENUM_COLORS.DARKEST
                  }
                >
                  {i18next.t('ORDERS.ORDERS.LIST_LABEL_ANOMALY_PROOF', { count: 1 })} :
                </Text>
                <Tooltip
                  text={i18next.t('ORDERS.ORDERS.LIST_LABEL_ANOMALY_PROOF_TOOLTIP', { count: 1 })}
                />
              </AnomaliePictureLabel>
              <MultiplePictureUpload
                disableAddPicture={allCreditPicturesHandlers.disableAddCreditPicture}
                disabledTooltipText={
                  disableAnomalyDropdown
                    ? null
                    : i18next.t('ORDERS.ORDERS.MAX_CREDIT_PICTURES_COUNT_REACHED_TOOLTIP', {
                        maxPicturesCount: MAX_CREDIT_ORDER_PICTURE_PER_ORDER,
                      })
                }
                displayDropdownActions={displayDropdownActions}
                initialPictures={anomalyPictures}
                isSizeTooHeavy={isSizeTooHeavy}
                multipleFiles={false}
                readOnly={disableAnomalyDropdown || readOnly}
                ref={selectedPictureRef}
                setDisplayDropdownActions={setDisplayDropdownActions}
                setIsSizeTooHeavy={setIsSizeTooHeavy}
                onSelectionChange={onSelectedPictureChange}
              />
            </List>
          </>
        )}

        {orderStatus <= ORDER_STATUS.RECEPTION && product.addedAfterOrdered && !hasBLI && (
          <>
            {isEditingPreparationOrder && <Divider />}
            <List>
              <RemoveReferenceContainer
                isDisabled={isRemoveAddedReferenceDisabled}
                onClick={handleRemoveAddedReference}
              >
                <img
                  alt="cross remove reference"
                  src={
                    isRemoveAddedReferenceDisabled
                      ? '/images/inpulse/close-lmgrey-small.svg'
                      : '/images/inpulse/close-black-small.svg'
                  }
                />
                <RemoveReference isDisabled={isRemoveAddedReferenceDisabled}>
                  {i18next.t('ORDERS.ORDERS.FORM_DELETE_REFERENCE_TITLE')}
                </RemoveReference>
              </RemoveReferenceContainer>
            </List>
          </>
        )}
      </ContentContainer>
      <Divider />
      <Footer
        customStyle={{
          'box-shadow': 'none',
          'background-color': 'transparent',
          width: 'auto',
          height: 'calc(100% - calc(100% - (64px + 24px)))',
          padding: '24px 0px',
        }}
      >
        <ButtonsContainer>
          <Button
            buttonCustomStyle={buttonStyle}
            color={'inpulse-outline'}
            handleClick={onClose}
            icon={'/images/inpulse/close-black-small.svg'}
            label={i18next.t(readOnly ? 'GENERAL.CLOSE' : 'GENERAL.CANCEL')}
            minWidth={168}
          />
          {orderStatus <= ORDER_STATUS.RECEPTION && (
            <Button
              buttonCustomStyle={buttonStyle}
              color={'inpulse-default'}
              handleClick={handleValidation}
              icon={'/images/inpulse/check-white-small.svg'}
              isDisabled={isValidationDisabled}
              label={i18next.t('GENERAL.VALIDATE')}
              minWidth={168}
            />
          )}
        </ButtonsContainer>
      </Footer>
    </Container>
  );
};

OrderReceptionDrawerContent.propTypes = {
  handleInputChange: PropTypes.func.isRequired,
  handleReceptionInputChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  priceBDL: PropTypes.number.isRequired,
  product: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  setPriceBDL: PropTypes.func.isRequired,
  handleReceptionAnomaly: PropTypes.func.isRequired,
  setOpenStockInfo: PropTypes.func.isRequired,
};

OrderReceptionDrawerContent.defaultProps = {
  readOnly: false,
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  authorizedActions: getAuthorizedActions(state.baseReducer.userRights, '/order/orders'),
});

export default connect(mapStateToProps)(OrderReceptionDrawerContent);
